import { useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import {
  ApiDownloadButton,
  LinkButton,
  LoadingIndicator,
  Pagination
} from 'components/common';
import { PageEmptyIndicator, PageSectionHeader } from 'components/page';
import { AchievementsAdminTile } from '.';

import type { ViewProps } from 'types';
import type { GetAchievementsRes } from 'shared-types/responses';

const AchievementsViewAdministered = ({
  description,
  hideIfNoData = false,
  isHomeView = false,
  limitPerPage = 12,
  onNoData = null,
  queryParams = '',
  title
}: ViewProps) => {
  const { messages } = useReduxState();

  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [queryParams]);

  const { data, loading } = useApiData<GetAchievementsRes>({
    url:
      `/admin/achievements?type=administered&achievementsPerPage=${limitPerPage}&page=${page}&includeAsSupervisor=true` +
      queryParams
  });

  useEffect(() => {
    const hasData = !loading && Boolean(data?.achievements?.length);
    if (!loading && onNoData) onNoData(hasData);
  }, [data, loading]);

  return (
    <div className="achievements-list">
      {loading ? (
        <LoadingIndicator />
      ) : data?.achievements?.length ? (
        <>
          <PageSectionHeader
            description={description}
            title={title || messages.coursesAdministered}
            titleCount={data?.achievementsCount}
          >
            {isHomeView ? null : (
              <ApiDownloadButton
                category="success"
                disabled={!data?.achievementsIds.length}
                filename={`${messages.commonAchievements}.xlsx`}
                icon="file-excel"
                packet={{ achievementIds: data?.achievementsIds || [] }}
                size="header"
                text={messages.tasksExportTasksData}
                url="/admin/achievements/excel-export"
              />
            )}
          </PageSectionHeader>

          <div className="achievements">
            {data.achievements.map((achievement) => (
              <div className="achievement" key={achievement.id}>
                <AchievementsAdminTile achievement={achievement} />
              </div>
            ))}
          </div>

          {data?.achievements?.length && data?.pages > 1 && !isHomeView ? (
            <Pagination
              currentPage={page}
              numberOfPages={data?.pages || 1}
              onPageChange={({ page }) => setPage(page)}
            />
          ) : null}
        </>
      ) : hideIfNoData ? null : (
        <>
          <PageSectionHeader
            description={description}
            title={title || messages.coursesAdministered}
            titleCount={0}
          />

          <PageEmptyIndicator
            icon="check-circle"
            title={messages.achievementsEmpty}
            description={messages.achievementsEmptyAdministered}
          >
            <LinkButton
              category="light"
              href="/achievements?type=all"
              size="small"
              text={messages.achievementsSeeAllAchievements}
            />
          </PageEmptyIndicator>
        </>
      )}

      <style jsx>{`
        .achievements {
          display: grid;
          gap: 30px;
          grid-template-columns: repeat(3, 1fr);
          grid-template-rows: auto;
          height: 100%;
        }
        @media (max-width: ${breakpoints.large - 1}px) {
          .achievements {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: ${breakpoints.small - 1}px) {
          .achievements {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default AchievementsViewAdministered;
